<template>
  <BasicBlock :title="title" :simple="!first">
    <template
      v-slot:titleAfter
      v-if="first && formResource.currency_setting === 'choice'"
    >
      <div class="f-w-16">
        <label :for="selectUuid">
          <select
            class="f-form-select f-border-0 f-float-right f-mr-2 f-rounded f-p-1 f-text-xs f-w-full f-text-gray-700"
            :id="selectUuid"
            v-model="chosenCurrency"
            @change="$emit('currencyChanged', $event.target.value)"
          >
            <option
              v-for="currencyOption in formResource.allowed_currencies"
              :key="currencyOption"
              :value="currencyOption"
            >
              {{ currencyOption }}
            </option>
          </select>
        </label>
      </div>
    </template>

    <Period
      v-if="first"
      :allowedPeriods="allowedPeriods"
      @periodChanged="$emit('periodChanged', $event)"
    ></Period>

    <ValidationProvider
      slim
      :rules="rulesObject"
      :skipIfEmpty="false"
      v-slot="{ errors }"
    >
      <input type="hidden" :name="itemGroup.id + '_items'" v-model="value" />

      <SimpleErrorLine
        v-if="errors.length > 0 && requiredItemIsSoldOut()"
        :message="$t('items.validations.group.requiredItemIsSoldOut')"
      ></SimpleErrorLine>
      <SimpleErrorLine
        v-else-if="errors.length > 0"
        :message="$t('items.validations.group.required')"
      ></SimpleErrorLine>
    </ValidationProvider>

    <Items
      :currency="currency"
      :itemGroup="itemGroup"
      :showPricesIncludingVat="formResource.show_prices_including_vat"
      :renderLikeRadio="renderLikeRadio"
      :selectedItems="groupSelectedItems()"
      :isVatPayer="isVatPayer"
      :selectedPeriod="selectedPeriod"
      :formResource="formResource"
      @itemDataChanged="itemDataChanged"
    ></Items>
  </BasicBlock>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import Items from "@/components/Form/Items/Items.vue";
import BasicBlock from "@/components/Form/BasicBlock.vue";
import FormItemGroup from "@/module/FormItemGroup";
import { ValidationProvider } from "vee-validate";
import { extend } from "vee-validate";
import SelectedItem from "@/module/Form/SelectedItem";
import FormResource from "@/module/Form/Resources/FormResource";
import Period from "@/components/Form/Items/Period.vue";
import { isStringOrNull } from "@/module/ComponentPropsValidations/validations";
import SimpleErrorLine from "@/components/Form/SimpleErrorLine.vue";

interface ComponentData {
  value: number;
  chosenCurrency: string | null;
  renderLikeRadio: boolean;
}

export default Vue.extend({
  name: "ItemGroup",

  components: {
    SimpleErrorLine,
    Period,
    Items,
    BasicBlock,
    ValidationProvider,
  },

  props: {
    itemGroup: {
      type: Object as PropType<FormItemGroup>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    first: {
      type: Boolean,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    selectedItems: {
      type: Array as PropType<Array<SelectedItem>>,
      required: true,
    },
    isVatPayer: {
      type: Boolean,
      required: true,
    },
    allowedPeriods: {
      type: Array as PropType<Array<string>>,
      required: true,
    },
    selectedPeriod: {
      required: true,
      validator: isStringOrNull,
    },
  },

  data(): ComponentData {
    return {
      value: 0,
      chosenCurrency: null,
      renderLikeRadio: false,
    };
  },

  computed: {
    title(): string {
      return this.first
        ? this.$t("items.block.title").toString()
        : this.itemGroup.title;
    },

    selectUuid(): string {
      return this.$id("currency-choice");
    },

    rulesObject(): object {
      return this.itemGroup.required
        ? {
            required: true,
            min_value: 1,
            isRequiredItemSoldOut: true,
          }
        : {};
    },
  },

  methods: {
    requiredItemIsSoldOut() {
      return this.itemGroup.items.some((item) => item.required && item.soldOut);
    },

    groupSelectedItems(): SelectedItem[] {
      return this.selectedItems.filter((selectedItem) => {
        return (
          this.itemGroup.items.find((formItem) => {
            return formItem.id === selectedItem.id;
          }) !== undefined
        );
      });
    },

    itemDataChanged(data: SelectedItem): void {
      this.$emit("itemDataChanged", data);

      this.$nextTick(() => {
        this.countSelectedAndVisibleItems();
      });
    },

    countSelectedAndVisibleItems(): void {
      let selectedItemCount = 0;

      for (const data of this.groupSelectedItems()) {
        if (!data.selected) {
          continue;
        }

        selectedItemCount += data.count;
      }

      this.value = selectedItemCount;
    },

    getValidationState(failed: boolean, errors: Array<string>): boolean | null {
      return failed ? (errors.length === 0 ? null : false) : null;
    },
  },

  created(): void {
    this.chosenCurrency = this.currency;
    this.renderLikeRadio = !this.itemGroup.allowMultipleItems;
    extend("isRequiredItemSoldOut", {
      validate: () => !this.requiredItemIsSoldOut(),
    });
  },
});
</script>

<style></style>
